import { writeSceneLog, writeSceneLogStyled } from '../utils/writeSceneLog.js';
import { LongFrameDetector } from './LongFrameDetector.js';

var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), member.set(obj, value), value);
var __privateWrapper = (obj, member, setter, getter) => ({
  set _(value) {
    __privateSet(obj, member, value);
  },
  get _() {
    return __privateGet(obj, member, getter);
  }
});
var _profileInProgress, _interactionInProgress, _profileStartTs, _trailAnimationFrameId, _recordedTrailingSpans, _longFrameDetector, _longFramesCount, _longFramesTotalTime, _visibilityChangeHandler, _onInteractionComplete;
const POST_STORM_WINDOW = 2e3;
const DEFAULT_LONG_FRAME_THRESHOLD = 30;
const TAB_INACTIVE_THRESHOLD = 1e3;
class SceneRenderProfiler {
  constructor(queryController) {
    this.queryController = queryController;
    __privateAdd(this, _profileInProgress, null);
    __privateAdd(this, _interactionInProgress, null);
    __privateAdd(this, _profileStartTs, null);
    __privateAdd(this, _trailAnimationFrameId, null);
    // Will keep measured lengths trailing frames
    __privateAdd(this, _recordedTrailingSpans, []);
    // Long frame tracking
    __privateAdd(this, _longFrameDetector);
    __privateAdd(this, _longFramesCount, 0);
    __privateAdd(this, _longFramesTotalTime, 0);
    __privateAdd(this, _visibilityChangeHandler, null);
    __privateAdd(this, _onInteractionComplete, null);
    this.measureTrailingFrames = (measurementStartTs, lastFrameTime, profileStartTs) => {
      var _a, _b, _c, _d;
      const currentFrameTime = performance.now();
      const frameLength = currentFrameTime - lastFrameTime;
      if (frameLength > TAB_INACTIVE_THRESHOLD) {
        writeSceneLog("SceneRenderProfiler", "Tab was inactive, cancelling profile measurement");
        this.cancelProfile();
        return;
      }
      __privateGet(this, _recordedTrailingSpans).push(frameLength);
      if (currentFrameTime - measurementStartTs < POST_STORM_WINDOW) {
        if (__privateGet(this, _profileInProgress)) {
          __privateSet(this, _trailAnimationFrameId, requestAnimationFrame(
            () => this.measureTrailingFrames(measurementStartTs, currentFrameTime, profileStartTs)
          ));
        }
      } else {
        const slowFrames = processRecordedSpans(__privateGet(this, _recordedTrailingSpans));
        const slowFramesTime = slowFrames.reduce((acc, val) => acc + val, 0);
        writeSceneLog(
          "SceneRenderProfiler",
          `Profile tail recorded - Slow frames: ${slowFramesTime.toFixed(1)}ms (${slowFrames.length} frames)`
        );
        writeSceneLog("", `  \u251C\u2500 Origin: ${((_a = __privateGet(this, _profileInProgress)) == null ? void 0 : _a.origin) || "unknown"}`);
        writeSceneLog("", `  \u2514\u2500 Crumbs:`, ((_b = __privateGet(this, _profileInProgress)) == null ? void 0 : _b.crumbs) || []);
        __privateSet(this, _recordedTrailingSpans, []);
        const profileDuration = measurementStartTs - profileStartTs;
        if (typeof performance !== "undefined" && performance.mark) {
          const profileName = ((_c = __privateGet(this, _profileInProgress)) == null ? void 0 : _c.origin) || "unknown";
          const totalTime = profileDuration + slowFramesTime;
          performance.mark(`Dashboard Profile End: ${profileName}`);
          const startMarkName = `Dashboard Profile Start: ${profileName}`;
          try {
            performance.measure(
              `Dashboard Profile: ${profileName} (${totalTime.toFixed(1)}ms)`,
              startMarkName,
              `Dashboard Profile End: ${profileName}`
            );
          } catch (e) {
            performance.mark(`Dashboard Profile Complete: ${profileName} (${totalTime.toFixed(1)}ms)`);
          }
          if (slowFrames.length > 0) {
            const slowFramesMarkName = `Slow Frames Summary: ${slowFrames.length} frames (${slowFramesTime.toFixed(
              1
            )}ms)`;
            performance.mark(slowFramesMarkName);
            slowFrames.forEach((frameTime, index) => {
              if (frameTime > 16) {
                try {
                  const frameStartTime = __privateGet(this, _profileStartTs) + profileDuration + (index > 0 ? slowFrames.slice(0, index).reduce((sum, t) => sum + t, 0) : 0);
                  const frameId = `slow-frame-${index}`;
                  const frameStartMark = `${frameId}-start`;
                  const frameEndMark = `${frameId}-end`;
                  performance.mark(frameStartMark, { startTime: frameStartTime });
                  performance.mark(frameEndMark, { startTime: frameStartTime + frameTime });
                  performance.measure(`Slow Frame ${index + 1}: ${frameTime.toFixed(1)}ms`, frameStartMark, frameEndMark);
                } catch (e) {
                  performance.mark(`Slow Frame ${index + 1}: ${frameTime.toFixed(1)}ms`);
                }
              }
            });
          }
        }
        const completionTimestamp = performance.now();
        writeSceneLog("SceneRenderProfiler", "Profile completed");
        writeSceneLog("", `  \u251C\u2500 Timestamp: ${completionTimestamp.toFixed(1)}ms`);
        writeSceneLog("", `  \u251C\u2500 Total time: ${(profileDuration + slowFramesTime).toFixed(1)}ms`);
        writeSceneLog("", `  \u251C\u2500 Slow frames: ${slowFramesTime}ms (${slowFrames.length} frames)`);
        writeSceneLog("", `  \u2514\u2500 Long frames: ${__privateGet(this, _longFramesTotalTime)}ms (${__privateGet(this, _longFramesCount)} frames)`);
        __privateGet(this, _longFrameDetector).stop();
        writeSceneLogStyled(
          "SceneRenderProfiler",
          `Stopped long frame detection - profile complete at ${completionTimestamp.toFixed(1)}ms`,
          "color: #00CC00; font-weight: bold;"
        );
        __privateSet(this, _trailAnimationFrameId, null);
        const profileEndTs = profileStartTs + profileDuration + slowFramesTime;
        if (!__privateGet(this, _profileInProgress)) {
          return;
        }
        performance.measure(`DashboardInteraction ${__privateGet(this, _profileInProgress).origin}`, {
          start: profileStartTs,
          end: profileEndTs
        });
        const networkDuration = captureNetwork(profileStartTs, profileEndTs);
        if (((_d = this.queryController) == null ? void 0 : _d.state.onProfileComplete) && __privateGet(this, _profileInProgress)) {
          this.queryController.state.onProfileComplete({
            origin: __privateGet(this, _profileInProgress).origin,
            crumbs: __privateGet(this, _profileInProgress).crumbs,
            duration: profileDuration + slowFramesTime,
            networkDuration,
            startTs: profileStartTs,
            endTs: profileEndTs,
            longFramesCount: __privateGet(this, _longFramesCount),
            longFramesTotalTime: __privateGet(this, _longFramesTotalTime),
            // @ts-ignore
            jsHeapSizeLimit: performance.memory ? performance.memory.jsHeapSizeLimit : 0,
            // @ts-ignore
            usedJSHeapSize: performance.memory ? performance.memory.usedJSHeapSize : 0,
            // @ts-ignore
            totalJSHeapSize: performance.memory ? performance.memory.totalJSHeapSize : 0
          });
          __privateSet(this, _profileInProgress, null);
          __privateSet(this, _trailAnimationFrameId, null);
        }
        if (window.__runs) {
          window.__runs += `${Date.now()}, ${profileDuration + slowFramesTime}
`;
        } else {
          window.__runs = `${Date.now()}, ${profileDuration + slowFramesTime}
`;
        }
      }
    };
    __privateSet(this, _longFrameDetector, new LongFrameDetector());
    this.setupVisibilityChangeHandler();
    __privateSet(this, _interactionInProgress, null);
  }
  setQueryController(queryController) {
    this.queryController = queryController;
  }
  setInteractionCompleteHandler(handler) {
    __privateSet(this, _onInteractionComplete, handler != null ? handler : null);
  }
  setupVisibilityChangeHandler() {
    if (__privateGet(this, _visibilityChangeHandler)) {
      return;
    }
    __privateSet(this, _visibilityChangeHandler, () => {
      if (document.hidden && __privateGet(this, _profileInProgress)) {
        writeSceneLog("SceneRenderProfiler", "Tab became inactive, cancelling profile");
        this.cancelProfile();
      }
    });
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", __privateGet(this, _visibilityChangeHandler));
    }
  }
  cleanup() {
    if (__privateGet(this, _visibilityChangeHandler) && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", __privateGet(this, _visibilityChangeHandler));
      __privateSet(this, _visibilityChangeHandler, null);
    }
    __privateGet(this, _longFrameDetector).stop();
    this.cancelProfile();
  }
  startProfile(name) {
    if (document.hidden) {
      writeSceneLog("SceneRenderProfiler", "Tab is inactive, skipping profile", name);
      return;
    }
    if (__privateGet(this, _profileInProgress)) {
      if (__privateGet(this, _trailAnimationFrameId)) {
        this.cancelProfile();
        this._startNewProfile(name, true);
      } else {
        this.addCrumb(name);
      }
    } else {
      this._startNewProfile(name);
    }
  }
  startInteraction(interaction) {
    if (__privateGet(this, _interactionInProgress)) {
      writeSceneLog("profile", "Cancelled interaction:", __privateGet(this, _interactionInProgress));
      __privateSet(this, _interactionInProgress, null);
    }
    __privateSet(this, _interactionInProgress, {
      interaction,
      startTs: performance.now()
    });
    writeSceneLog("SceneRenderProfiler", "Started interaction:", interaction);
  }
  stopInteraction() {
    if (!__privateGet(this, _interactionInProgress)) {
      return;
    }
    const endTs = performance.now();
    const interactionDuration = endTs - __privateGet(this, _interactionInProgress).startTs;
    const networkDuration = captureNetwork(__privateGet(this, _interactionInProgress).startTs, endTs);
    writeSceneLog("SceneRenderProfiler", "Completed interaction:");
    writeSceneLog("", `  \u251C\u2500 Total time: ${interactionDuration.toFixed(1)}ms`);
    writeSceneLog("", `  \u251C\u2500 Network duration: ${networkDuration.toFixed(1)}ms`);
    writeSceneLog("", `  \u251C\u2500 StartTs: ${__privateGet(this, _interactionInProgress).startTs.toFixed(1)}ms`);
    writeSceneLog("", `  \u2514\u2500 EndTs: ${endTs.toFixed(1)}ms`);
    if (__privateGet(this, _onInteractionComplete) && __privateGet(this, _profileInProgress)) {
      __privateGet(this, _onInteractionComplete).call(this, {
        origin: __privateGet(this, _interactionInProgress).interaction,
        duration: interactionDuration,
        networkDuration,
        startTs: __privateGet(this, _interactionInProgress).startTs,
        endTs
      });
    }
    performance.mark(`${__privateGet(this, _interactionInProgress).interaction}_start`, {
      startTime: __privateGet(this, _interactionInProgress).startTs
    });
    performance.mark(`${__privateGet(this, _interactionInProgress).interaction}_end`, {
      startTime: endTs
    });
    performance.measure(
      `Interaction_${__privateGet(this, _interactionInProgress).interaction}`,
      `${__privateGet(this, _interactionInProgress).interaction}_start`,
      `${__privateGet(this, _interactionInProgress).interaction}_end`
    );
    __privateSet(this, _interactionInProgress, null);
  }
  getCurrentInteraction() {
    var _a, _b;
    return (_b = (_a = __privateGet(this, _interactionInProgress)) == null ? void 0 : _a.interaction) != null ? _b : null;
  }
  /**
   * Starts a new profile for performance measurement.
   *
   * @param name - The origin/trigger of the profile (e.g., 'time_range_change', 'variable_value_changed')
   * @param force - Whether this is a "forced" profile (true) or "clean" profile (false)
   *               - "forced": Started by canceling an existing profile that was recording trailing frames
   *                           This happens when a new user interaction occurs before the previous one
   *                           finished measuring its performance impact
   *               - "clean": Started when no profile is currently active
   */
  _startNewProfile(name, force = false) {
    var _a;
    __privateSet(this, _profileInProgress, { origin: name, crumbs: [] });
    __privateSet(this, _profileStartTs, performance.now());
    __privateSet(this, _longFramesCount, 0);
    __privateSet(this, _longFramesTotalTime, 0);
    if (typeof performance !== "undefined" && performance.mark) {
      const markName = `Dashboard Profile Start: ${name}`;
      performance.mark(markName);
    }
    writeSceneLogStyled(
      "SceneRenderProfiler",
      `Profile started[${force ? "forced" : "clean"}]`,
      "color: #FFCC00; font-weight: bold;"
    );
    writeSceneLog("", `  \u251C\u2500 Origin: ${((_a = __privateGet(this, _profileInProgress)) == null ? void 0 : _a.origin) || "unknown"}`);
    writeSceneLog("", `  \u2514\u2500 Timestamp: ${__privateGet(this, _profileStartTs).toFixed(1)}ms`);
    __privateGet(this, _longFrameDetector).start((event) => {
      if (!__privateGet(this, _profileInProgress) || !__privateGet(this, _profileStartTs)) {
        return;
      }
      if (event.timestamp < __privateGet(this, _profileStartTs)) {
        return;
      }
      __privateWrapper(this, _longFramesCount)._++;
      __privateSet(this, _longFramesTotalTime, __privateGet(this, _longFramesTotalTime) + event.duration);
    });
  }
  recordProfileTail(measurementStartTime, profileStartTs) {
    __privateSet(this, _trailAnimationFrameId, requestAnimationFrame(
      () => this.measureTrailingFrames(measurementStartTime, measurementStartTime, profileStartTs)
    ));
  }
  tryCompletingProfile() {
    var _a;
    if (!__privateGet(this, _profileInProgress)) {
      return;
    }
    writeSceneLog("SceneRenderProfiler", "Trying to complete profile", __privateGet(this, _profileInProgress));
    if (((_a = this.queryController) == null ? void 0 : _a.runningQueriesCount()) === 0 && __privateGet(this, _profileInProgress)) {
      writeSceneLog("SceneRenderProfiler", "All queries completed, starting tail measurement");
      this.recordProfileTail(performance.now(), __privateGet(this, _profileStartTs));
    }
  }
  isTailRecording() {
    return Boolean(__privateGet(this, _trailAnimationFrameId));
  }
  cancelTailRecording() {
    if (__privateGet(this, _trailAnimationFrameId)) {
      cancelAnimationFrame(__privateGet(this, _trailAnimationFrameId));
      __privateSet(this, _trailAnimationFrameId, null);
      writeSceneLog("SceneRenderProfiler", "Cancelled recording frames, new profile started");
    }
  }
  // cancel profile
  cancelProfile() {
    if (__privateGet(this, _profileInProgress)) {
      writeSceneLog("SceneRenderProfiler", "Cancelling profile", __privateGet(this, _profileInProgress));
      __privateSet(this, _profileInProgress, null);
      if (__privateGet(this, _trailAnimationFrameId)) {
        cancelAnimationFrame(__privateGet(this, _trailAnimationFrameId));
        __privateSet(this, _trailAnimationFrameId, null);
      }
      __privateGet(this, _longFrameDetector).stop();
      writeSceneLog("SceneRenderProfiler", "Stopped long frame detection - profile cancelled");
      __privateSet(this, _recordedTrailingSpans, []);
      __privateSet(this, _longFramesCount, 0);
      __privateSet(this, _longFramesTotalTime, 0);
    }
  }
  addCrumb(crumb) {
    if (__privateGet(this, _profileInProgress)) {
      writeSceneLog("SceneRenderProfiler", "Adding crumb:", crumb);
      __privateGet(this, _profileInProgress).crumbs.push(crumb);
    }
  }
}
_profileInProgress = new WeakMap();
_interactionInProgress = new WeakMap();
_profileStartTs = new WeakMap();
_trailAnimationFrameId = new WeakMap();
_recordedTrailingSpans = new WeakMap();
_longFrameDetector = new WeakMap();
_longFramesCount = new WeakMap();
_longFramesTotalTime = new WeakMap();
_visibilityChangeHandler = new WeakMap();
_onInteractionComplete = new WeakMap();
function processRecordedSpans(spans) {
  for (let i = spans.length - 1; i >= 0; i--) {
    if (spans[i] > DEFAULT_LONG_FRAME_THRESHOLD) {
      return spans.slice(0, i + 1);
    }
  }
  return [spans[0]];
}
function captureNetwork(startTs, endTs) {
  const entries = performance.getEntriesByType("resource");
  performance.clearResourceTimings();
  const networkEntries = entries.filter(
    (entry) => entry.startTime >= startTs && entry.startTime <= endTs && entry.responseEnd >= startTs && entry.responseEnd <= endTs
  );
  for (const entry of networkEntries) {
    performance.measure("Network entry " + entry.name, {
      start: entry.startTime,
      end: entry.responseEnd
    });
  }
  return calculateNetworkTime(networkEntries);
}
function calculateNetworkTime(requests) {
  if (requests.length === 0) {
    return 0;
  }
  requests.sort((a, b) => a.startTime - b.startTime);
  let totalNetworkTime = 0;
  let currentStart = requests[0].startTime;
  let currentEnd = requests[0].responseEnd;
  for (let i = 1; i < requests.length; i++) {
    if (requests[i].startTime <= currentEnd) {
      currentEnd = Math.max(currentEnd, requests[i].responseEnd);
    } else {
      totalNetworkTime += currentEnd - currentStart;
      currentStart = requests[i].startTime;
      currentEnd = requests[i].responseEnd;
    }
  }
  totalNetworkTime += currentEnd - currentStart;
  return totalNetworkTime;
}
const REFRESH_INTERACTION = "refresh";
const TIME_RANGE_CHANGE_INTERACTION = "time_range_change";
const FILTER_REMOVED_INTERACTION = "filter_removed";
const FILTER_CHANGED_INTERACTION = "filter_changed";
const FILTER_RESTORED_INTERACTION = "filter_restored";
const VARIABLE_VALUE_CHANGED_INTERACTION = "variable_value_changed";
const SCOPES_CHANGED_INTERACTION = "scopes_changed";
const ADHOC_KEYS_DROPDOWN_INTERACTION = "adhoc_keys_dropdown";
const ADHOC_VALUES_DROPDOWN_INTERACTION = "adhoc_values_dropdown";
const GROUPBY_DIMENSIONS_INTERACTION = "groupby_dimensions";

export { ADHOC_KEYS_DROPDOWN_INTERACTION, ADHOC_VALUES_DROPDOWN_INTERACTION, FILTER_CHANGED_INTERACTION, FILTER_REMOVED_INTERACTION, FILTER_RESTORED_INTERACTION, GROUPBY_DIMENSIONS_INTERACTION, REFRESH_INTERACTION, SCOPES_CHANGED_INTERACTION, SceneRenderProfiler, TIME_RANGE_CHANGE_INTERACTION, VARIABLE_VALUE_CHANGED_INTERACTION, calculateNetworkTime, captureNetwork, processRecordedSpans };
//# sourceMappingURL=SceneRenderProfiler.js.map
